import React, { useState } from 'react';
import {
  ArrowRight,
  BookOpen,
  LifeBuoy,
  Mail,
  MessageCircle,
  Plus,
  Search,
  Send,
  Zap
} from 'lucide-react';
import Footer from './Footer';

const SupportPage = ({ showPage }) => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to articles with search query
      showPage(`/articles?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="page active">
      {/* HERO SECTION */}
      <section className="pt-16 pb-20 bg-gradient-to-br from-teal-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold mb-6">
            <LifeBuoy className="w-3 h-3" /> Support Center
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-neutral-900 mb-6">How can we help?</h1>
          <p className="text-xl text-neutral-600 mb-10 max-w-2xl mx-auto">Search our help center or reach out to our team. We're here 7 days a week.</p>
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <form onSubmit={handleSearchSubmit}>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search for help... e.g. 'buy electricity', 'pay rent'"
                className="w-full pl-12 pr-4 py-4 rounded-large border border-neutral-200 bg-white shadow-custom text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
              />
            </form>
          </div>
        </div>
      </section>

      {/* CONTACT OPTIONS */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="text-center p-8 rounded-large bg-background-50 border border-neutral-100 hover:shadow-custom transition-all">
              <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-large flex items-center justify-center mx-auto mb-5">
                <MessageCircle className="w-7 h-7" />
              </div>
              <h3 className="font-heading text-lg font-bold text-neutral-900 mb-2">Live Chat</h3>
              <p className="text-neutral-600 text-sm mb-5">Chat with our support team in real time.</p>
              <button className="inline-flex items-center justify-center px-5 py-2.5 bg-primary-600 text-white text-sm font-semibold rounded-small hover:bg-primary-700 transition-colors gap-2">
                <Zap className="w-4 h-4" /> Start Chat
              </button>
            </div>
            <div className="text-center p-8 rounded-large bg-background-50 border border-neutral-100 hover:shadow-custom transition-all">
              <div className="w-14 h-14 bg-green-100 text-green-600 rounded-large flex items-center justify-center mx-auto mb-5">
                <Mail className="w-7 h-7" />
              </div>
              <h3 className="font-heading text-lg font-bold text-neutral-900 mb-2">Email Support</h3>
              <p className="text-neutral-600 text-sm mb-5">Send us a detailed message.</p>
              <a href="mailto:support@bilm.app" className="inline-flex items-center justify-center px-5 py-2.5 border border-neutral-300 text-neutral-900 text-sm font-semibold rounded-small hover:bg-neutral-50 transition-colors gap-2">
                <Send className="w-4 h-4" /> Send Email
              </a>
            </div>
            <div className="text-center p-8 rounded-large bg-background-50 border border-neutral-100 hover:shadow-custom transition-all">
              <div className="w-14 h-14 bg-purple-100 text-purple-600 rounded-large flex items-center justify-center mx-auto mb-5">
                <BookOpen className="w-7 h-7" />
              </div>
              <h3 className="font-heading text-lg font-bold text-neutral-900 mb-2">Help Articles</h3>
              <p className="text-neutral-600 text-sm mb-5">Browse our library of guides.</p>
              <button 
                onClick={() => showPage('/articles')}
                className="inline-flex items-center justify-center px-5 py-2.5 border border-neutral-300 text-neutral-900 text-sm font-semibold rounded-small hover:bg-neutral-50 transition-colors gap-2"
              >
                <ArrowRight className="w-4 h-4" /> Browse Articles
              </button>
            </div>
          </div>

          {/* FAQ SECTION */}
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl font-bold text-neutral-900 mb-10 text-center">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {/* Electricity FAQs */}
              <div className="border-2 border-amber-200 rounded-large overflow-hidden">
                <button
                  onClick={() => toggleFaq(0)}
                  className="w-full flex justify-between items-center p-5 text-left bg-amber-50 hover:bg-amber-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-amber-500">⚡</span>
                    <span className="font-semibold text-neutral-900 text-sm">How do I buy electricity on Bilm?</span>
                  </div>
                  <Plus className={`w-5 h-5 text-neutral-400 shrink-0 transition-transform ${expandedFaq === 0 ? 'rotate-45' : ''}`} />
                </button>
                {expandedFaq === 0 && (
                  <div className="bg-white border-t border-amber-100">
                    <div className="p-5 text-sm text-neutral-600 leading-relaxed">
                      <p className="mb-3">Buying electricity on Bilm is quick and easy:</p>
                      <ol className="list-decimal list-inside space-y-2">
                        <li>Open the Bilm app and tap the ⚡ Electricity icon</li>
                        <li>Your meter number will be pre-filled (added by your manager)</li>
                        <li>Select your desired amount (₦500 – ₦20,000)</li>
                        <li>Confirm the purchase — your token arrives in under 10 seconds</li>
                        <li>Enter the token on your prepaid meter and you're powered up!</li>
                      </ol>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-2 border-amber-200 rounded-large overflow-hidden">
                <button
                  onClick={() => toggleFaq(1)}
                  className="w-full flex justify-between items-center p-5 text-left bg-amber-50 hover:bg-amber-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-amber-500">⚡</span>
                    <span className="font-semibold text-neutral-900 text-sm">My electricity token didn't work. What do I do?</span>
                  </div>
                  <Plus className={`w-5 h-5 text-neutral-400 shrink-0 transition-transform ${expandedFaq === 1 ? 'rotate-45' : ''}`} />
                </button>
                {expandedFaq === 1 && (
                  <div className="bg-white border-t border-amber-100">
                    <div className="p-5 text-sm text-neutral-600 leading-relaxed">
                      <ul className="list-disc list-inside space-y-2">
                        <li>Double-check you entered all 20 digits correctly</li>
                        <li>Ensure your meter is in "vend" mode</li>
                        <li>Check that the meter number matches your physical meter</li>
                        <li>If the issue persists, contact support — we'll reissue within 30 minutes</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-2 border-amber-200 rounded-large overflow-hidden">
                <button
                  onClick={() => toggleFaq(2)}
                  className="w-full flex justify-between items-center p-5 text-left bg-amber-50 hover:bg-amber-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-amber-500">⚡</span>
                    <span className="font-semibold text-neutral-900 text-sm">How do I check my electricity balance?</span>
                  </div>
                  <Plus className={`w-5 h-5 text-neutral-400 shrink-0 transition-transform ${expandedFaq === 2 ? 'rotate-45' : ''}`} />
                </button>
                {expandedFaq === 2 && (
                  <div className="bg-white border-t border-amber-100">
                    <div className="p-5 text-sm text-neutral-600 leading-relaxed">
                      Your current electricity balance is displayed on your Bilm home screen. Tap the electricity card to see your full purchase history and estimated remaining units. Enable low-balance notifications in Settings.
                    </div>
                  </div>
                )}
              </div>

              {/* Other FAQs */}
              <div className="border border-neutral-200 rounded-large overflow-hidden">
                <button
                  onClick={() => toggleFaq(3)}
                  className="w-full flex justify-between items-center p-5 text-left bg-white hover:bg-neutral-50 transition-colors"
                >
                  <span className="font-semibold text-neutral-900 text-sm">How do I pay my rent through Bilm?</span>
                  <Plus className={`w-5 h-5 text-neutral-400 shrink-0 transition-transform ${expandedFaq === 3 ? 'rotate-45' : ''}`} />
                </button>
                {expandedFaq === 3 && (
                  <div className="bg-neutral-50 border-t border-neutral-100">
                    <div className="p-5 text-sm text-neutral-600 leading-relaxed">
                      Open the Bilm app and tap "Pay Rent" on your home screen. Choose your payment method (bank transfer, card, or USSD), confirm the amount, and complete the payment. You'll receive a receipt instantly via email and in-app.
                    </div>
                  </div>
                )}
              </div>

              <div className="border border-neutral-200 rounded-large overflow-hidden">
                <button
                  onClick={() => toggleFaq(4)}
                  className="w-full flex justify-between items-center p-5 text-left bg-white hover:bg-neutral-50 transition-colors"
                >
                  <span className="font-semibold text-neutral-900 text-sm">How do I submit a maintenance request?</span>
                  <Plus className={`w-5 h-5 text-neutral-400 shrink-0 transition-transform ${expandedFaq === 4 ? 'rotate-45' : ''}`} />
                </button>
                {expandedFaq === 4 && (
                  <div className="bg-neutral-50 border-t border-neutral-100">
                    <div className="p-5 text-sm text-neutral-600 leading-relaxed">
                      Tap "Maintenance" from your home screen, then tap "New Request." Select the issue category, describe the problem, attach photos if needed, and submit. You'll receive real-time updates as your request is processed.
                    </div>
                  </div>
                )}
              </div>

              <div className="border border-neutral-200 rounded-large overflow-hidden">
                <button
                  onClick={() => toggleFaq(5)}
                  className="w-full flex justify-between items-center p-5 text-left bg-white hover:bg-neutral-50 transition-colors"
                >
                  <span className="font-semibold text-neutral-900 text-sm">Can I set up autopay for rent?</span>
                  <Plus className={`w-5 h-5 text-neutral-400 shrink-0 transition-transform ${expandedFaq === 5 ? 'rotate-45' : ''}`} />
                </button>
                {expandedFaq === 5 && (
                  <div className="bg-neutral-50 border-t border-neutral-100">
                    <div className="p-5 text-sm text-neutral-600 leading-relaxed">
                      Yes! Go to Payments → Autopay in the app. Set your preferred payment date and method, and Bilm will automatically process your rent each month. You'll receive a reminder 3 days before each payment.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer showPage={showPage} />
    </div>
  );
};

export default SupportPage;
